import React, { useState, useEffect } from 'react';
import { X, Check } from 'lucide-react';

export interface DistributorRecord {
  id: number;
  name: string;
  companyName?: string;
  phone: string;
  zone?: string;
  city: string;
  zoneCity?: string;
  depo: string;
  route: string;
  status?: 'Active' | 'Inactive';
  createdOn?: string;
  email?: string;
  address?: string;
  code?: string;
  sapCode?: string;
  pan?: string;
  fssai?: string;
}

interface AddEditDistributorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (distributor: Partial<DistributorRecord>) => void;
  initialData?: DistributorRecord | null;
}

export const AddEditDistributorModal: React.FC<AddEditDistributorModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
}) => {
  const isEditing = Boolean(initialData);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [zone, setZone] = useState('West');
  const [city, setCity] = useState('Mumbai');
  const [depo, setDepo] = useState('Mumbai Depo');
  const [route, setRoute] = useState('R-101');
  const [status, setStatus] = useState<'Active' | 'Inactive'>('Active');
  const [address, setAddress] = useState('');

  useEffect(() => {
    queueMicrotask(() => {
      if (initialData) {
        setName(initialData.name || '');
        setPhone(initialData.phone || '');
        setEmail(initialData.email || '');
        setZone(initialData.zone || 'West');
        setCity(initialData.city || 'Mumbai');
        setDepo(initialData.depo || 'Mumbai Depo');
        setRoute(initialData.route || 'R-101');
        setStatus(initialData.status || 'Active');
        setAddress(initialData.address || '');
      } else {
        setName('');
        setPhone('');
        setEmail('');
        setZone('West');
        setCity('Mumbai');
        setDepo('Mumbai Depo');
        setRoute('R-101');
        setStatus('Active');
        setAddress('');
      }
    });
  }, [initialData, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) return;

    onSave({
      id: initialData ? initialData.id : Date.now(),
      name,
      phone,
      email,
      zone,
      city,
      zoneCity: `${zone} → ${city}`,
      depo,
      route,
      status,
      address,
      createdOn: initialData ? initialData.createdOn : '21 Aug 2026',
    });

    onClose();
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 shadow-2xl border border-slate-100 animate-fadeIn space-y-6">
        {/* Modal Header */}
        <div className="flex items-center justify-between pb-4 border-b border-slate-100">
          <div>
            <h3 className="text-lg font-extrabold text-slate-800 tracking-tight">
              {isEditing ? 'Edit Distributor' : 'Add New Distributor'}
            </h3>
            <p className="text-xs font-medium text-slate-400 mt-0.5">
              {isEditing ? 'Update distributor information below' : 'Fill details to add a new distributor'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center cursor-pointer transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Modal Form */}
        <form onSubmit={handleSubmit} className="space-y-4 text-xs">
          {/* Name & Phone row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="font-bold text-slate-700">Distributor Name *</label>
              <input
                type="text"
                required
                placeholder="e.g. Shree Balaji Distributors"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#C1272D] focus:ring-1 focus:ring-[#C1272D]"
              />
            </div>

            <div className="space-y-1">
              <label className="font-bold text-slate-700">Phone Number *</label>
              <input
                type="text"
                required
                placeholder="e.g. 9876543210"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#C1272D] focus:ring-1 focus:ring-[#C1272D]"
              />
            </div>
          </div>

          {/* Email */}
          <div className="space-y-1">
            <label className="font-bold text-slate-700">Email Address</label>
            <input
              type="email"
              placeholder="e.g. distributor@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#C1272D] focus:ring-1 focus:ring-[#C1272D]"
            />
          </div>

          {/* Zone & City */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="font-bold text-slate-700">Zone</label>
              <select
                value={zone}
                onChange={(e) => setZone(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-slate-800 focus:outline-none focus:border-[#C1272D] cursor-pointer"
              >
                <option value="North">North</option>
                <option value="West">West</option>
                <option value="South">South</option>
                <option value="East">East</option>
                <option value="Central">Central</option>
              </select>
            </div>

            <div className="space-y-1">
              <label className="font-bold text-slate-700">City</label>
              <input
                type="text"
                placeholder="e.g. Delhi / Mumbai"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#C1272D]"
              />
            </div>
          </div>

          {/* Depo & Route */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="font-bold text-slate-700">Depo</label>
              <input
                type="text"
                placeholder="e.g. Delhi Depo"
                value={depo}
                onChange={(e) => setDepo(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#C1272D]"
              />
            </div>

            <div className="space-y-1">
              <label className="font-bold text-slate-700">Route</label>
              <input
                type="text"
                placeholder="e.g. R-101"
                value={route}
                onChange={(e) => setRoute(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#C1272D]"
              />
            </div>
          </div>

          {/* Status Radio Buttons */}
          <div className="space-y-1 pt-1">
            <label className="font-bold text-slate-700 block">Status</label>
            <div className="flex items-center gap-6 pt-1">
              <label className="flex items-center gap-2 cursor-pointer font-semibold text-slate-700">
                <input
                  type="radio"
                  name="status"
                  value="Active"
                  checked={status === 'Active'}
                  onChange={() => setStatus('Active')}
                  className="accent-[#C1272D]"
                />
                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-md text-[11px] font-bold">
                  Active
                </span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer font-semibold text-slate-700">
                <input
                  type="radio"
                  name="status"
                  value="Inactive"
                  checked={status === 'Inactive'}
                  onChange={() => setStatus('Inactive')}
                  className="accent-[#C1272D]"
                />
                <span className="px-2 py-0.5 bg-rose-100 text-rose-700 rounded-md text-[11px] font-bold">
                  Inactive
                </span>
              </label>
            </div>
          </div>

          {/* Address */}
          <div className="space-y-1 pt-1">
            <label className="font-bold text-slate-700">Address</label>
            <textarea
              rows={2}
              placeholder="Enter full address details..."
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#C1272D]"
            />
          </div>

          {/* Buttons Footer */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 border border-slate-200 text-slate-600 font-bold rounded-xl hover:bg-slate-50 cursor-pointer transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-5 py-2.5 bg-[#C1272D] hover:bg-[#A81E23] text-white font-bold rounded-xl shadow-md shadow-[#C1272D]/20 cursor-pointer flex items-center gap-1.5 transition-all"
            >
              <Check className="w-4 h-4" />
              <span>{isEditing ? 'Save Changes' : 'Add Distributor'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
